import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type {
  CreateRecurringCostInput,
  RecurringCost,
  RecurringCostCategory,
  UpdateRecurringCostInput,
} from "../../lib/realestate";
import { Modal } from "../ui/Modal";

const CATEGORIES: RecurringCostCategory[] = [
  "HAUSGELD",
  "VERSICHERUNG",
  "INSTANDHALTUNG",
  "GRUNDSTEUER",
  "SONSTIGES",
];

interface Props {
  open: boolean;
  defaultCategory?: RecurringCostCategory;
  existing: RecurringCost | null;
  submitting: boolean;
  onClose: () => void;
  onSubmit: (input: CreateRecurringCostInput | UpdateRecurringCostInput) => Promise<void>;
}

function parseEuroDe(input: string): number {
  const trimmed = input.replace(/\./g, "").replace(",", ".").trim();
  const n = Number.parseFloat(trimmed);
  if (Number.isNaN(n) || n < 0) throw new Error("invalid_amount");
  return Math.round(n * 100);
}

function centsToEuroDe(cents: number): string {
  return (cents / 100).toFixed(2).replace(".", ",");
}

function initialCategory(
  existing: RecurringCost | null,
  defaultCategory: RecurringCostCategory | undefined,
): RecurringCostCategory {
  if (existing) return existing.category;
  return defaultCategory ?? "HAUSGELD";
}

export function RecurringCostFormModal({
  open,
  defaultCategory,
  existing,
  submitting,
  onClose,
  onSubmit,
}: Props) {
  const { t } = useTranslation();
  const [category, setCategory] = useState<RecurringCostCategory>(
    initialCategory(existing, defaultCategory),
  );
  const [effectiveFrom, setEffectiveFrom] = useState(existing?.effectiveFrom ?? "");
  const [amount, setAmount] = useState(existing ? centsToEuroDe(existing.monthlyAmountCents) : "");
  const [notes, setNotes] = useState(existing?.notes ?? "");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setCategory(initialCategory(existing, defaultCategory));
    setEffectiveFrom(existing?.effectiveFrom ?? "");
    setAmount(existing ? centsToEuroDe(existing.monthlyAmountCents) : "");
    setNotes(existing?.notes ?? "");
    setError(null);
  }, [existing, defaultCategory]);

  const title = existing
    ? t("realestate.recurring.form_title_edit")
    : t("realestate.recurring.add_title");

  async function handleSubmit() {
    setError(null);
    try {
      const trimmedNotes = notes.trim();
      const input: CreateRecurringCostInput | UpdateRecurringCostInput = {
        category,
        effectiveFrom,
        monthlyAmountCents: parseEuroDe(amount),
        notes: trimmedNotes === "" ? null : trimmedNotes,
      };
      await onSubmit(input);
      if (!existing) {
        setAmount("");
        setNotes("");
      }
      onClose();
    } catch {
      setError(t("realestate.errors.invalid_amount"));
    }
  }

  return (
    <Modal open={open} onClose={onClose} title={title}>
      <div className="space-y-3">
        <label className="block">
          <span className="text-sm">{t("realestate.recurring.category")}</span>
          <select
            className="block mt-1 w-full border rounded px-2 py-1"
            value={category}
            onChange={(e) => setCategory(e.target.value as RecurringCostCategory)}
          >
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {t(`realestate.recurring.category_label.${c}`)}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-sm">{t("realestate.recurring.effective_from")}</span>
          <input
            type="date"
            className="block mt-1 w-full border rounded px-2 py-1"
            value={effectiveFrom}
            onChange={(e) => setEffectiveFrom(e.target.value)}
            required
          />
        </label>
        <label className="block">
          <span className="text-sm">{t("realestate.recurring.monthly_amount")}</span>
          <input
            inputMode="decimal"
            className="block mt-1 w-full border rounded px-2 py-1"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="250,00"
            required
          />
        </label>
        <label className="block">
          <span className="text-sm">{t("realestate.recurring.notes")}</span>
          <textarea
            className="block mt-1 w-full border rounded px-2 py-1"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            maxLength={500}
            rows={2}
          />
        </label>
        {error && <div className="text-red-700 text-sm">{error}</div>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-1 border rounded">
            {t("common.cancel")}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="px-3 py-1 bg-blue-600 text-white rounded"
          >
            {submitting ? t("common.submitting") : t("common.save")}
          </button>
        </div>
      </div>
    </Modal>
  );
}
